/**
 * `Paper` — a physical sheet of label stock. `docs/design-direction.md` §6.11.
 *
 * Cream ground, black UI type, 1px `--cool-ink` ruling at 18% on a 28px baseline that
 * the 15px/1.45 body locks onto, a `clip-path` so no edge is machine-perfect, a −0.8°
 * rotation, and one or two grain-masked tape strips at the top. The recipe is
 * `.paper` / `.paper__tape` in `styles/texture.css`.
 *
 * **Paper appears in exactly two places in the whole app**: the label on the disc and
 * the Queue set-list sheet (§6.11). It is the only large light area in a dark
 * art-directed object, which is why it is always framed as an object rather than as a
 * theme: an inverted cream block never exceeds 25% of viewport height and never
 * appears twice at once, the single exception being the set-list sheet, which *is*
 * the interaction and may fill 80% while it is open (§5.5.6, §8.4).
 *
 * **It is all or nothing.** If a surface cannot rotate, tape, hard-cut and rule, it
 * must be dark instead — a half-committed light screen reads as a rendering bug
 * (§12.14). That is why this component has no "flat" variant and no way to switch the
 * rotation or the clip off: the only choice it offers is how many tape strips hold it
 * down.
 *
 * Copy on paper: black `--text-on-paper` UI type, and the hand numbering down the
 * left margin in Caveat 600 — which is one of exactly two licensed uses of that face
 * (the other is the chinagraph title on a label), and `[data-scrawl="off"]` replaces
 * it with what a plant would have typeset instead rather than faking handwriting
 * (§4.4, §12.15).
 */

import type { JSX, ReactNode } from 'react';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface PaperProps {
  readonly children?: ReactNode;
  /**
   * Tape strips holding the sheet down. Two (the default) is the set-list sheet's
   * pair at the top corners; one is a single strip at 22%; zero is a sheet that is
   * held by nothing, which is right for the label inside a disc, where the groove
   * ring already frames it.
   */
  readonly tape?: 0 | 1 | 2;
  /** Element to render. Default `div`; `section` when the sheet is a real section. */
  readonly as?: 'div' | 'section' | 'article';
  readonly className?: string | undefined;
}

export function Paper({ children, tape = 2, as = 'div', className }: PaperProps): JSX.Element {
  const Tag = as;
  const strips = tape === 0 ? [] : tape === 1 ? [1] : [1, 2];

  return (
    <Tag className={cx('paper', styles.paper, className)} data-paper="true">
      {strips.map((strip) => (
        <span
          key={strip}
          className={cx('paper__tape', styles.tape)}
          data-strip={String(strip)}
          aria-hidden="true"
        />
      ))}
      {children}
    </Tag>
  );
}
